import { useMemo, useSyncExternalStore, type MouseEvent } from 'react';
import { flushSync } from 'react-dom';
import type { TerminalRelayState } from './features/terminal/relay-status';

function subscribeToRoute(callback: () => void): () => void {
  window.addEventListener('popstate', callback);
  return () => { window.removeEventListener('popstate', callback); };
}

/** Snapshot for `useSyncExternalStore`: a stable primitive, never a freshly built array. */
function currentRoutePath(): string {
  return window.location.pathname.replace(/^\//, '');
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** The only subscription to the address bar: `/terminal/Steven/kant` → ['terminal','Steven','kant']. */
export function useRouteSegments(): readonly string[] {
  const path = useSyncExternalStore(subscribeToRoute, currentRoutePath, () => '');
  return useMemo(() => path.split('/').filter(Boolean).map(decodeSegment), [path]);
}

export function useRouteSearch(): string {
  return useSyncExternalStore(subscribeToRoute, () => window.location.search, () => '');
}

/** Navigates without reloading. `pushState` does not fire `popstate` on its own, so we dispatch it
    by hand so the router —which subscribes to `popstate`— notices the change. */
export function navigate(path: string): void {
  if (`${window.location.pathname}${window.location.search}` === path) return;
  // Inside one section (chat to chat) a full-page cross-fade only adds latency; the launcher opts in.
  const sameSection = path.split(/[/?]/u)[1] === window.location.pathname.split('/')[1] && !('chatLaunch' in document.documentElement.dataset);
  window.history.pushState({}, '', path);
  const announce = () => { window.dispatchEvent(new PopStateEvent('popstate')); };
  const transitions = document as Document & { startViewTransition?: (update: () => void) => unknown };
  if (sameSection || typeof transitions.startViewTransition !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    announce();
    return;
  }
  transitions.startViewTransition(() => { flushSync(announce); });
}

/**
 * Replaces the current history entry instead of stacking a new one. This is what fits a retired
 * route that redirects to its heir: with `pushState`, the "back" button would return to the dead
 * route, which redirects forward again, and the operator would get stuck unable to leave.
 */
export function redirect(path: string): void {
  if (`${window.location.pathname}${window.location.search}` === path) return;
  window.history.replaceState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Links keep their real `href` so middle-click, ctrl+click, "open in new tab" and the context
 * menu continue to work. Only the plain left-click is intercepted, because that is the only one
 * that should stay inside the app.
 *
 * `disabledReason`: if present, the entry is disabled (see `terminalNavAvailability` below) and
 * the click must not navigate under any circumstance — not even with a modifier — because the
 * destination has nothing real behind it.
 */
export function onNavClick(event: MouseEvent<HTMLAnchorElement>, path: string, disabledReason?: string): void {
  if (event.defaultPrevented) return;
  if (disabledReason) {
    event.preventDefault();
    return;
  }
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigate(path);
}

/** How a menu entry whose availability depends on an optional backend should be presented. */
export interface NavEntryAvailability {
  /** `true`: do not render the entry. */
  hidden: boolean;
  /** `true`: render it inert (without navigating) with `reason` as the explanation. */
  disabled: boolean;
  /** Single-line reason, present whenever `disabled` is `true`. */
  reason?: string;
}

/**
 * Determines the navigation availability for the terminal entry based on the relay's state.
 */
export function terminalNavAvailability(relay: TerminalRelayState): NavEntryAvailability {
  if (relay.status !== 'unavailable') return { hidden: false, disabled: false };
  return { hidden: false, disabled: true, reason: relay.reason };
}

export const CONFIG_SIN_CONTROL_REASON =
  'Tu cuenta no tiene permiso de control sobre esta flota: Configuración es del dueño del bus.';

/** `GET /v3/console/config` demands `read`; only the mutations demand `control`. */
export const CONFIG_SIN_LECTURA_REASON =
  'Tu cuenta no tiene permiso de LECTURA sobre esta flota: sin «read» el servidor no entrega la '
  + 'configuración. El permiso de control es otro y solo hace falta para cambiar algo.';

export const CONFIG_WRITE_NO_ACREDITADO_REASON =
  'No se pudo acreditar config.write; la vista permanece disponible en solo lectura y no permite cambios ni restauraciones.';
